import React from "react";
import {
    ClockAlert,
    HelpCircle,
    RedoDot,
    CalendarClock,
    AlertTriangle,
    MinusCircle,
    Clock,
    Calendar,
    FileQuestion,
    PauseCircle
} from "lucide-react";

export enum AppraiserIndicator {
    OVER_DUE = 'order overdue',
    REVISION_REQUEST = 'revision request',
    DUE_TODAY = 'due today',
    ACCEPTANCE_RESPONSE_REQUIRED = 'acceptance response required',
    LATE_ACCEPTANCE = 'late acceptance',
    ORDER_REJECTED = 'order rejected',
    INSPECTION_DATE_NOT_SET = 'inspection date not set',
    LATE_INSPECTION_DATE_SET = 'late inspection date set',
    INSPECTION_OVERDUE = 'inspection overdue',
    DESKTOP_FOLLOWUP = 'desktop followup',
    ON_HOLD = 'on hold',
}

// Define the color types we support (matching Tailwind's color palette)
export type ColorName = 'red' | 'purple' | 'yellow' | 'orange' | 'blue' | 'teal' | 'gray';

export interface AppraiserIndicatorMetadata {
    icon: React.ReactNode;
    color: ColorName;
    bgColor: string;
    textColor: string;
    iconColor: string;
}

// Define all indicator metadata directly with hardcoded color classes
export const appraiserIndicatorMetadata: Record<AppraiserIndicator, AppraiserIndicatorMetadata> = {
    [AppraiserIndicator.OVER_DUE]: {
        icon: <ClockAlert />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-500'
    },
    [AppraiserIndicator.REVISION_REQUEST]: {
        icon: <RedoDot />,
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        iconColor: 'text-purple-500'
    },
    [AppraiserIndicator.DUE_TODAY]: {
        icon: <Clock />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [AppraiserIndicator.ACCEPTANCE_RESPONSE_REQUIRED]: {
        icon: <AlertTriangle />,
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        iconColor: 'text-orange-500'
    },
    [AppraiserIndicator.LATE_ACCEPTANCE]: {
        icon: <ClockAlert />,
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-900',
        iconColor: 'text-orange-600'
    },
    [AppraiserIndicator.ORDER_REJECTED]: {
        icon: <MinusCircle />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-900',
        iconColor: 'text-red-600'
    },
    [AppraiserIndicator.INSPECTION_DATE_NOT_SET]: {
        icon: <Calendar />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        iconColor: 'text-blue-500'
    },
    [AppraiserIndicator.LATE_INSPECTION_DATE_SET]: {
        icon: <CalendarClock />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-900',
        iconColor: 'text-blue-600'
    },
    [AppraiserIndicator.INSPECTION_OVERDUE]: {
        icon: <ClockAlert />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-500'
    },
    [AppraiserIndicator.DESKTOP_FOLLOWUP]: {
        icon: <FileQuestion />,
        color: 'teal',
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-800',
        iconColor: 'text-teal-500'
    },
    [AppraiserIndicator.ON_HOLD]: {
        icon: <PauseCircle />,
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        iconColor: 'text-gray-500'
    },
};

export const defaultAppraiserIndicatorMetaData: AppraiserIndicatorMetadata = {
    icon: <HelpCircle />,
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    iconColor: 'text-gray-500'
};

// Utility function to get metadata for a specific status
export const getAppraiserIndicatorMeta = (indicatorMessage: string): AppraiserIndicatorMetadata => {
    const normalized = indicatorMessage.toLowerCase();
    if (Object.values(AppraiserIndicator).includes(normalized as AppraiserIndicator)) {
        return appraiserIndicatorMetadata[normalized as AppraiserIndicator];
    }
    return defaultAppraiserIndicatorMetaData;
};